import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Plus, Trash, TrendUp, Lightbulb } from '@phosphor-icons/react';
import { toast } from 'sonner';
import MarketNews from '../components/MarketNews';
import LoadingBar from '../components/LoadingBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Investments = () => {
  const [investments, setInvestments] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [newInvestment, setNewInvestment] = useState({ name: '', type: '', amount: '', return_rate: '', date: new Date().toISOString().split('T')[0] });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invRes, sugRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/investments`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/investments/suggestions`, { withCredentials: true })
      ]);
      setInvestments(invRes.data);
      setSuggestions(sugRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/investments`, {
        ...newInvestment,
        amount: parseFloat(newInvestment.amount),
        return_rate: parseFloat(newInvestment.return_rate)
      }, { withCredentials: true });
      toast.success('Investimento registrado!');
      setOpen(false);
      fetchData();
      setNewInvestment({ name: '', type: '', amount: '', return_rate: '', date: new Date().toISOString().split('T')[0] });
    } catch (error) {
      toast.error('Erro ao registrar investimento');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/investments/${id}`, { withCredentials: true });
      toast.success('Investimento removido');
      fetchData();
    } catch (error) {
      toast.error('Erro ao remover');
    }
  };

  const getRiskColor = (risk) => {
    switch(risk) {
      case 'Baixo': return 'bg-green-100 text-green-700';
      case 'Médio': return 'bg-yellow-100 text-yellow-700';
      case 'Alto': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) return <LoadingBar text="Carregando investimentos..." />;

  const totalInvested = investments.reduce((sum, inv) => sum + inv.amount, 0);
  const avgReturn = investments.length > 0 ? investments.reduce((sum, inv) => sum + inv.return_rate, 0) / investments.length : 0;

  return (
    <div className="p-3 sm:p-6 md:p-8 space-y-6" data-testid="investments-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight mb-1 sm:mb-2">Investimentos</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Gerencie seus investimentos e veja sugestões</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-investment-button"><Plus size={20} className="mr-2" />Registrar Investimento</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrar Investimento</DialogTitle>
              <DialogDescription>Adicione um novo investimento ao portfólio</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div><Label>Nome</Label><Input value={newInvestment.name} onChange={(e) => setNewInvestment({...newInvestment, name: e.target.value})} data-testid="investment-name" required /></div>
              <div><Label>Tipo</Label><Input value={newInvestment.type} onChange={(e) => setNewInvestment({...newInvestment, type: e.target.value})} data-testid="investment-type" placeholder="Ex: Ações, CDB, Tesouro" required /></div>
              <div><Label>Valor Investido</Label><Input type="number" step="0.01" value={newInvestment.amount} onChange={(e) => setNewInvestment({...newInvestment, amount: e.target.value})} data-testid="investment-amount" required /></div>
              <div><Label>Taxa de Retorno (%)</Label><Input type="number" step="0.01" value={newInvestment.return_rate} onChange={(e) => setNewInvestment({...newInvestment, return_rate: e.target.value})} data-testid="investment-return" required /></div>
              <div><Label>Data</Label><Input type="date" value={newInvestment.date} onChange={(e) => setNewInvestment({...newInvestment, date: e.target.value})} data-testid="investment-date" required /></div>
              <Button type="submit" className="w-full" data-testid="submit-investment">Registrar</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center space-x-2">
              <TrendUp size={18} />
              <span>Total Investido</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold tabular-nums text-blue-600" data-testid="total-invested">
              R$ {totalInvested.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center space-x-2">
              <TrendUp size={18} />
              <span>Retorno Médio</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold tabular-nums text-green-600" data-testid="avg-return">
              {(avgReturn ?? 0).toFixed(2)}%
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="portfolio" className="w-full">
        <TabsList>
          <TabsTrigger value="portfolio" data-testid="tab-portfolio">Meu Portfólio</TabsTrigger>
          <TabsTrigger value="suggestions" data-testid="tab-suggestions">Sugestões</TabsTrigger>
        </TabsList>
        
        <TabsContent value="portfolio" className="space-y-4">
          {investments.length === 0 ? (
            <Card><CardContent className="pt-6 text-center text-muted-foreground">Nenhum investimento registrado</CardContent></Card>
          ) : (
            investments.map(inv => (
              <Card key={inv.investment_id} data-testid={`investment-${inv.investment_id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-lg">
                          <TrendUp size={24} weight="bold" />
                        </div>
                        <div>
                          <p className="font-semibold text-lg">{inv.name}</p>
                          <p className="text-sm text-muted-foreground">{inv.type} • {new Date(inv.date).toLocaleDateString('pt-BR')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <p className="text-2xl font-bold tabular-nums text-blue-600">
                          R$ {inv.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </p>
                        <p className="text-sm text-green-600 font-semibold">+{inv.return_rate}% a.a.</p>
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(inv.investment_id)} data-testid={`delete-investment-${inv.investment_id}`}>
                        <Trash size={20} className="text-destructive" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
        
        <TabsContent value="suggestions" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {suggestions.map((sug, idx) => (
              <Card key={idx} data-testid={`suggestion-${idx}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      <Lightbulb size={24} weight="fill" className="text-yellow-500" />
                      <CardTitle>{sug.name}</CardTitle>
                    </div>
                    <Badge className={getRiskColor(sug.risk)}>{sug.risk}</Badge>
                  </div>
                  <CardDescription>{sug.type}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground">{sug.description}</p>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-sm text-muted-foreground">Retorno Esperado</span>
                    <span className="text-xl font-bold text-green-600">+{sug.expected_return}%</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Market News Section */}
      <MarketNews />
    </div>
  );
};

export default Investments;
